import { Dispatch, FC, SetStateAction, useCallback, useMemo } from "react";
import { usePreviousDifferent } from "rooks";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { grey } from "@mui/material/colors";
import {
  AgDataGrid,
  AgDataGridProps,
} from "@/components/pages/common/ag-grid/AgDataGrid";
import { useAppSelector } from "@/store/hooks";
import { resourceExplorerDataGridDataSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data-grid/resourceExplorerDataGridDataSelector";
import { resourceExplorerGroupingSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerGroupingSelector";
import { getBillingExplorerDataGridColumnDefs } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/column-defs/billingExplorerTotalViewColumnDefs";

interface BillingExplorerTotalViewGridProps {
  setGridApi: Dispatch<SetStateAction<GridApi | null>>;
  rowHoverProps: Pick<AgDataGridProps, "onCellMouseOver" | "onCellMouseOut">;
}

export const BillingExplorerTotalViewGrid: FC<
  BillingExplorerTotalViewGridProps
> = ({ setGridApi, rowHoverProps }) => {
  const gridData = useAppSelector(resourceExplorerDataGridDataSelector);
  const groupBy = useAppSelector(resourceExplorerGroupingSelector);
  const prevData = usePreviousDifferent(gridData);

  const columnDefs = useMemo(() => {
    return getBillingExplorerDataGridColumnDefs(groupBy);
  }, [groupBy]);

  const data = gridData ?? prevData;

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);

      setTimeout(() => {
        event.api.applyColumnState({
          state: [{ colId: "field", pinned: "left" }],
          defaultState: { pinned: null },
        });
      });
    },
    [setGridApi],
  );

  if (!data) {
    return null;
  }

  return (
    <AgDataGrid
      key={groupBy}
      rowData={data}
      headerHeight={60}
      rowHeight={50}
      columnDefs={columnDefs}
      onGridReady={onGridReady}
      defaultColDef={defaultColDef}
      wrapperStyles={wrapperStyles}
      configToRememberStateInStorage={configToRememberStateInStorage}
      {...rowHoverProps}
    />
  );
};

const configToRememberStateInStorage = {
  uniqueNameAsKey: "billing-explorer-total-view",
};

const wrapperStyles = {
  border: 1,
  minHeight: 500,
  borderColor: grey[300],
  ".ag-header-cell-text": { width: "100%" },
};

const defaultColDef = {
  minWidth: 150,
  unSortIcon: true,
  flex: 1,
};
